import { useQuery } from '@tanstack/react-query';
import { useActiveAccount } from 'thirdweb/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import { get } from '../lib/api';
import axios from '../utils/axios-config';
import { API_ENDPOINTS, API_BASE_URL } from '../config/api-config';
import { useAuthStore } from '../stores';

/**
 * Check if user is logged in based on wallet connection
 * Integrates with Zustand auth store for state management
 */
export function useCheckUserLogin(options = {}) {
  const activeAccount = useActiveAccount();
  const address = useMemo(
    () => activeAccount?.address,
    [activeAccount?.address]
  );

  const { setUser, setLoading, updateWalletAddress } = useAuthStore();

  // Update wallet address when it changes
  useEffect(() => {
    if (address) {
      updateWalletAddress(address);
    }
  }, [address, updateWalletAddress]);

  const query = useQuery({
    queryKey: ['auth', 'login-status', address],
    queryFn: async () => {
      if (!address) {
        setUser(null);
        return false;
      }

      try {
        const isLogged = await get({
          url: `${API_BASE_URL}/omniverse/user/isLoggedIn`,
        });

        let result = false;
        if (typeof isLogged === 'boolean') {
          result = isLogged;
          // If logged in, fetch user data separately
          if (result) {
            try {
              const userResponse = await get({
                url: `${API_BASE_URL}/omniverse/user/${address}`,
              });
              if (userResponse && userResponse.user) {
                setUser(userResponse.user);
              } else if (userResponse) {
                setUser(userResponse);
              }
            } catch (userErr) {
              console.error(
                '[useCheckUserLogin] Failed to fetch user data:',
                userErr
              );
            }
          }
        } else if (typeof isLogged === 'object' && 'success' in isLogged) {
          result = isLogged.success;
          // If user data is returned, update store
          if (isLogged.user) {
            console.log('[useCheckUserLogin] Setting user:', isLogged.user);
            setUser(isLogged.user);
          } else {
            console.warn(
              '[useCheckUserLogin] API returned success but no user data'
            );
            // API says logged in but no user data - fetch user data separately
            if (result) {
              try {
                const userResponse = await get({
                  url: `${API_BASE_URL}/omniverse/user/${address}`,
                });
                console.log(
                  '[useCheckUserLogin] Fetched user data:',
                  userResponse
                );
                if (userResponse) {
                  setUser(userResponse);
                }
              } catch (userErr) {
                console.error(
                  '[useCheckUserLogin] Failed to fetch user data:',
                  userErr
                );
              }
            }
          }
        }

        if (!result) {
          setUser(null);
        }

        return result;
      } catch (err) {
        console.error('[useCheckUserLogin] Error:', err);
        setUser(null);
        return false;
      } finally {
        setLoading(false);
      }
    },
    enabled: !!address,
    staleTime: 30 * 1000, // 30 seconds - auth state is stable within a session
    gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
    refetchOnWindowFocus: false, // Don't refetch on window focus - reduces redundant calls
    refetchOnMount: false, // Use cached data on mount if available
    refetchOnReconnect: true, // Only refetch on network reconnection
    retry: 1,
    // Default to false when no address - prevents showing authenticated UI
    placeholderData: false,
    ...options,
  });

  return query;
}

/**
 * Get user by ID (wallet address)
 */
export function useGetUserById(id, options = {}) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const res = await axios.get(API_ENDPOINTS.USERS.GET_BY_ID(id));
        return res.data.user;
      } catch (error) {
        // Return null for 404 (user not found) instead of throwing
        // This prevents error states that cause re-renders
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: false, // Don't retry - 404 returns null, other errors will throw
    staleTime: 1000 * 30, // 30 seconds - allow refetch when data is stale
    gcTime: 1000 * 60 * 5, // 5 minutes - keep in cache longer
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Refetch on mount if data is stale (invalidated after discussion creation)
    refetchInterval: false,
    ...options,
  });
}

/**
 * Check if username is available
 * @param {string} username - Username to check
 * @param {string} currentUserOid - Current user's OID to exclude from check (for editing own profile)
 */
export function useCheckUsername(
  username,
  currentUserOid = null,
  options = {}
) {
  return useQuery({
    queryKey: ['username-check', username],
    queryFn: async () => {
      if (!username || username.length < 3) {
        return { available: true, checked: false };
      }
      const res = await axios.get(API_ENDPOINTS.USERS.CHECK_USERNAME(username));
      return {
        available: !res.data.exists,
        checked: true,
        message: res.data.message,
      };
    },
    enabled: !!username && username.length >= 3,
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60, // 1 minute
    ...options,
  });
}

/**
 * Get followers of a user
 */
export function useGetUserFollowers(userId, options = {}) {
  return useQuery({
    queryKey: ['user', userId, 'followers'],
    queryFn: async () => {
      if (!userId) return [];
      const res = await axios.get(API_ENDPOINTS.USERS.GET_FOLLOWERS(userId));
      return res.data;
    },
    enabled: !!userId,
    ...options,
  });
}

/**
 * Get users that a user is following
 */
export function useGetUserFollowing(userId, options = {}) {
  return useQuery({
    queryKey: ['user', userId, 'following'],
    queryFn: async () => {
      if (!userId) return [];
      const res = await axios.get(API_ENDPOINTS.USERS.GET_FOLLOWING(userId));
      return res.data;
    },
    enabled: !!userId,
    ...options,
  });
}

/**
 * Update user profile mutation
 */
export function useUpdateUserProfile(id, options = {}) {
  const queryClient = useQueryClient();
  const { user: currentUser, updateUser } = useAuthStore();

  return useMutation({
    mutationFn: async (data) => {
      if (!id) throw new Error('User ID required');
      const res = await axios.put(API_ENDPOINTS.USERS.UPDATE_PROFILE(id), data);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['user', id]);
      queryClient.invalidateQueries(['user']);
      if (data?.user) {
        queryClient.setQueryData(['user', id], data.user);

        // Also update auth store if this is the current user's profile
        // This ensures the navbar avatar updates immediately
        if (currentUser && (
          currentUser.passportId === data.user.passportId ||
          currentUser.oid === data.user.oid ||
          currentUser.creator === data.user.creator
        )) {
          updateUser(data.user);
        }
      }
    },
    ...options,
  });
}

/**
 * Follow a user mutation
 */
export function useFollowUser(userId, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('User ID required');
      const res = await axios.post(API_ENDPOINTS.USERS.FOLLOW(userId));
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user', userId, 'followers']);
    },
    ...options,
  });
}

/**
 * Unfollow a user mutation
 */
export function useUnfollowUser(userId, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('User ID required');
      const res = await axios.post(API_ENDPOINTS.USERS.UNFOLLOW(userId));
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user', userId, 'followers']);
    },
    ...options,
  });
}

/**
 * Submit registration (username, bio, avatar) mutation
 */
export function useSubmitRegistration(id, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ username, bio, avatar }) => {
      if (!id) throw new Error('User ID required');
      if (!username || !bio) {
        throw new Error('Username and bio are required');
      }
      const updateData = {
        username,
        bio,
        isVerified: true,
      };

      // Include avatar if provided
      if (avatar) {
        updateData.avatar = avatar;
      }

      const res = await axios.put(
        API_ENDPOINTS.USERS.UPDATE_PROFILE(id),
        updateData
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user', id]);
    },
    ...options,
  });
}

/**
 * Get all interactions by user ID
 */
export function useGetAllInteractionsByUserId(userId, options = {}) {
  return useQuery({
    queryKey: ['user', userId, 'interactions'],
    queryFn: async () => {
      if (!userId) return [];
      const res = await axios.get(
        API_ENDPOINTS.INTERACTIONS.GET_All_BY_USER_ID(userId)
      );
      return res.data;
    },
    enabled: !!userId,
    staleTime: 30000,
    cacheTime: 60000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    ...options,
  });
}

/**
 * Get interactions GIVEN by user (actions the user performed like upvotes, comments, etc.)
 */
export function useGetInteractionsGivenByUser(userOid, options = {}) {
  return useQuery({
    queryKey: ['user', userOid, 'interactions-given'],
    queryFn: async () => {
      if (!userOid) return null;
      const res = await axios.get(
        API_ENDPOINTS.INTERACTIONS.GET_GIVEN_BY_USER_OID(userOid)
      );
      return res.data;
    },
    enabled: !!userOid,
    staleTime: 30000,
    cacheTime: 60000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    ...options,
  });
}

/**
 * Get view stats for a user (unique, power, standard views given and received)
 */
export function useGetViewStats(userId, options = {}) {
  return useQuery({
    queryKey: ['user', userId, 'viewStats'],
    queryFn: async () => {
      if (!userId) return null;
      const res = await axios.get(API_ENDPOINTS.USERS.GET_VIEW_STATS(userId));
      return res.data.viewStats;
    },
    enabled: !!userId,
    staleTime: 5000, // 5 seconds - refresh quickly
    cacheTime: 30000, // 30 seconds
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    ...options,
  });
}

/**
 * Get user's view status on a specific piece of content
 */
export function useGetContentViewStatus(contentOid, userWallet, options = {}) {
  return useQuery({
    queryKey: ['viewStatus', contentOid, userWallet],
    queryFn: async () => {
      if (!contentOid || !userWallet) return null;
      const res = await axios.get(
        `${API_ENDPOINTS.USERS.GET_CONTENT_VIEW_STATUS(
          contentOid
        )}?userWallet=${userWallet}`
      );
      return res.data.viewStatus;
    },
    enabled: !!contentOid && !!userWallet,
    staleTime: 5000,
    cacheTime: 30000,
    refetchOnMount: true,
    ...options,
  });
}
